import { ViewportScroller } from '@angular/common';
import { ApplicationRef, ComponentRef, DestroyRef, Injectable, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, Scroll } from '@angular/router';
import { ScrollToService } from '@shared/v2';
import { WindowRef } from '@spartacus/core';
import { filter, pairwise } from 'rxjs/operators';

export const config = {
  enableResetViewOnNavigate: {
    active: true,
    ignoreQueryString: true,
    ignoreRoutes: [],
  },
};

@Injectable({
  providedIn: 'root',
})
export class OnNavigateService {
  get hostComponent(): ComponentRef<any> {
    return this.injector.get(ApplicationRef)?.components?.[0];
  }

  private clickHandler?: (event: MouseEvent) => void;
  private isInitialNavigation = true;

  constructor(
    protected router: Router,
    protected viewportScroller: ViewportScroller,
    protected injector: Injector,
    protected windowRef: WindowRef,
    protected destroyRef: DestroyRef,
    protected scrollToService: ScrollToService,
  ) {}

  /**
   * Reads configuration and enables features based on flags set.
   */
  initializeWithConfig(): void {
    if (this.windowRef.isBrowser() && this.windowRef.nativeWindow?.history) {
      this.windowRef.nativeWindow.history.scrollRestoration = 'manual';
    }

    if (config?.enableResetViewOnNavigate?.active) {
      this.setResetViewOnNavigate(config.enableResetViewOnNavigate.active);
    }
    this.setupAnchorScrolling();
  }

  /**
   * Resets view back to the original position when performing a back navigation and to the top when performing a front navigation
   * and sets the focus back to the top of the page before skiplinks for any type of navigation
   * @param enable Enable or disable this feature
   */
  setResetViewOnNavigate(enable: boolean): void {
    if (!enable || !this.windowRef.isBrowser()) {
      return;
    }

    this.router.events
      .pipe(
        filter((event): event is Scroll => event instanceof Scroll),
        pairwise(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        const previousRoute = event[0];
        const currentRoute = event[1];

        const hasHash =
          !!(this.windowRef.nativeWindow?.location?.hash && this.windowRef.nativeWindow.location.hash.length > 1) ||
          !!currentRoute.anchor;

        if (currentRoute.position) {
          // Навигация "назад" с сохраненной позицией
          if (!hasHash) {
            requestAnimationFrame(() => {
              if (currentRoute.position) {
                this.viewportScroller.scrollToPosition(currentRoute.position as [number, number]);
              }
            });
          }
        } else {
          // Навигация "вперед" без позиции
          // Проверка игнорирования query-параметров
          if (config.enableResetViewOnNavigate?.ignoreQueryString && this.isPathEqual(previousRoute, currentRoute)) {
            return;
          }

          // Проверка игнорирования дочерних маршрутов
          if (this.isChildRoute(currentRoute)) {
            return;
          }

          // Скролл к началу если нет хэша
          if (!hasHash) {
            this.viewportScroller.scrollToPosition([0, 0]);
          }
        }

        // Фокусировка на хост-компоненте
        this.hostComponent?.location?.nativeElement.focus?.();
      });
  }

  /**
   * Handles anchor scrolling with offset for mobile devices.
   * Intercepts NavigationEnd events with hash fragments and scrolls to the anchor element.
   * Also handles clicks on anchor links to ensure NavigationEnd is generated.
   */
  private setupAnchorScrolling(): void {
    if (!this.windowRef.isBrowser() || !this.windowRef.nativeWindow) {
      return;
    }

    if (this.clickHandler) {
      this.windowRef.nativeWindow.document.removeEventListener('click', this.clickHandler);
    }

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => {
        const hashIndex = event.urlAfterRedirects.indexOf('#');

        if (hashIndex !== -1) {
          const anchorId = event.urlAfterRedirects.substring(hashIndex + 1);

          if (this.isInitialNavigation) {
            this.isInitialNavigation = false;
            requestAnimationFrame(() => this.scrollToAnchor(anchorId));
          } else {
            this.scrollToAnchor(anchorId);
          }
        } else {
          this.isInitialNavigation = false;
        }
      });

    this.clickHandler = (event: MouseEvent): void => {
      const anchor = (event.target as HTMLElement).closest('a[href^="#"]') as HTMLAnchorElement;
      const anchorId = anchor?.getAttribute('href')?.substring(1);

      if (anchorId) {
        event.preventDefault();
        this.router.navigate([], { fragment: anchorId });
      }
    };

    this.windowRef.nativeWindow.document.addEventListener('click', this.clickHandler);
  }

  /**
   * Scrolls to anchor element with offset for mobile devices
   */
  private scrollToAnchor(anchorId: string): void {
    if (!this.windowRef.isBrowser() || !this.windowRef.nativeWindow) {
      return;
    }

    requestAnimationFrame(() => {
      this.scrollToService.scrollToId(anchorId, { offset: -70 });
    });
  }

  /**
   * Verifies if the current route is a child route from the given ignore config route
   *
   * @param route
   * @returns boolean whether the route is a child route
   */
  private isChildRoute(route: Scroll): boolean {
    const navigationEnd = route.routerEvent as NavigationEnd | undefined;
    const url = navigationEnd?.urlAfterRedirects || '';

    if (!url || !config.enableResetViewOnNavigate?.ignoreRoutes?.length) {
      return false;
    }

    const urlParts = url.split('/');

    return config.enableResetViewOnNavigate.ignoreRoutes.some(
      (configRoute) => configRoute && urlParts.includes(configRoute),
    );
  }

  /**
   * Verifies if the previous and current route are the same without the query string
   *
   * @param previousRoute
   * @param currentRoute
   * @returns boolean depending on the previous and current route are equal without the query strings
   */
  private isPathEqual(previousRoute: Scroll, currentRoute: Scroll): boolean {
    const prevNavigationEnd = previousRoute.routerEvent as NavigationEnd | undefined;
    const currNavigationEnd = currentRoute.routerEvent as NavigationEnd | undefined;

    const prevUrl = prevNavigationEnd?.urlAfterRedirects || '';
    const currUrl = currNavigationEnd?.urlAfterRedirects || '';

    if (!prevUrl || !currUrl) {
      return false;
    }

    return prevUrl.split('?')[0] === currUrl.split('?')[0];
  }
}
